import { useTranslation } from 'react-i18next';
import { Wrench, DollarSign, TrendingDown, Percent, CreditCard } from 'lucide-react';
import { simulateCashFlow } from './dealCalc';

const DIFF_CLS = {
  easy: 'text-emerald-500 bg-emerald-500/10',
  medium: 'text-amber-500 bg-amber-500/10',
  hard: 'text-red-500 bg-red-500/10',
};

export default function DealRescue({ deal }) {
  const { t } = useTranslation();

  if (deal.monthly_cash_flow > 100 && deal.deal_score >= 70) return null;

  const methods = [];

  if (deal.suggested_offer_price < deal.purchase_price) {
    const cf = simulateCashFlow(deal, { purchase_price: deal.suggested_offer_price });
    methods.push({
      key: 'negotiate', icon: TrendingDown, label: t('decision.negotiatePrice'),
      detail: `$${deal.purchase_price.toLocaleString()} → $${deal.suggested_offer_price.toLocaleString()}`,
      resultCF: cf, difficulty: 'easy', impact: Math.abs(cf - deal.monthly_cash_flow),
    });
  }

  if (deal.monthly_cash_flow < 0) {
    const eff = 1 - (deal.vacancy_rate || 5) / 100 - (deal.property_management_percent || 10) / 100;
    const inc = eff > 0 ? Math.abs(deal.monthly_cash_flow) / eff : Math.abs(deal.monthly_cash_flow);
    const pct = ((inc * 12) / deal.annual_income * 100).toFixed(1);
    const cf = simulateCashFlow(deal, { annual_income: deal.annual_income + inc * 12 });
    methods.push({
      key: 'rent', icon: DollarSign, label: t('decision.increaseRent'),
      detail: `+$${Math.round(inc).toLocaleString()}/mo (${pct}%)`,
      resultCF: cf, difficulty: Math.abs(deal.monthly_cash_flow) > 500 ? 'hard' : 'medium',
      impact: Math.abs(cf - deal.monthly_cash_flow),
    });
  }

  if (deal.down_payment_percent < 30) {
    const dp = Math.min(35, deal.down_payment_percent + 5);
    const cf = simulateCashFlow(deal, { down_payment_percent: dp });
    methods.push({
      key: 'dp', icon: CreditCard, label: t('decision.increaseDP'),
      detail: `${deal.down_payment_percent}% → ${dp}%`,
      resultCF: cf, difficulty: 'medium', impact: Math.abs(cf - deal.monthly_cash_flow),
    });
  }

  if (deal.interest_rate > 3) {
    const rate = Math.max(2.5, deal.interest_rate - 1);
    const cf = simulateCashFlow(deal, { interest_rate: rate });
    methods.push({
      key: 'rate', icon: Percent, label: t('decision.lowerRate'),
      detail: `${deal.interest_rate}% → ${rate}%`,
      resultCF: cf, difficulty: 'hard', impact: Math.abs(cf - deal.monthly_cash_flow),
    });
  }

  methods.sort((a, b) => b.impact - a.impact);
  if (!methods.length) return null;

  return (
    <div data-testid="deal-rescue" className="bg-white dark:bg-[#0F1419] border border-slate-200 dark:border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{t('decision.dealRescue')}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">{t('decision.dealRescueDesc')}</p>

      <div className="space-y-2.5">
        {methods.map((m, i) => (
          <div key={m.key} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-white/[0.02] rounded-lg">
            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-white/10 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <m.icon className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-sm font-semibold text-slate-900 dark:text-white">{m.label}</span>
                <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${DIFF_CLS[m.difficulty]}`}>
                  {t(`decision.${m.difficulty}`)}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-0.5">{m.detail}</p>
              <p className="text-xs">
                <span className="text-slate-400">{t('decision.resultingCF')}: </span>
                <span className={`font-bold tabular-nums ${m.resultCF >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {m.resultCF >= 0 ? '+' : ''}{m.resultCF.toLocaleString()}/mo
                </span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
